import { supabase } from '../lib/supabase';

type RawInboxMessage = {
  id: number;
  sender_id: string;
  receiver_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
};

export type InboxMessage = RawInboxMessage & {
  sender_nickname: string;
};

async function currentUserId(): Promise<string> {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) throw new Error('로그인이 필요합니다');
  return user.id;
}

async function nicknameMap(senderIds: string[]): Promise<Record<string, string>> {
  const ids = [...new Set(senderIds.filter(Boolean))];
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nickname')
    .in('id', ids);
  if (error) throw error;
  return Object.fromEntries((data || []).map((profile) => [
    profile.id,
    profile.nickname || '(탈퇴 회원)',
  ]));
}

export async function listInboxMessages({
  limit = 30,
  offset = 0,
}: {
  limit?: number;
  offset?: number;
} = {}): Promise<InboxMessage[]> {
  const userId = await currentUserId();
  const safeLimit = Math.min(100, Math.max(1, Math.floor(limit)));
  const safeOffset = Math.max(0, Math.floor(offset));
  const { data, error } = await supabase
    .from('messages')
    .select('id, sender_id, receiver_id, content, is_read, created_at')
    .eq('receiver_id', userId)
    .eq('is_deleted_by_receiver', false)
    .order('created_at', { ascending: false })
    .range(safeOffset, safeOffset + safeLimit - 1);
  if (error) throw error;

  const messages = (data || []) as RawInboxMessage[];
  const names = await nicknameMap(messages.map((message) => message.sender_id));
  return messages.map((message) => ({
    ...message,
    sender_nickname: names[message.sender_id] || '(탈퇴 회원)',
  }));
}

export async function getInboxMessage(id: number | string): Promise<InboxMessage | null> {
  const userId = await currentUserId();
  const { data, error } = await supabase
    .from('messages')
    .select('id, sender_id, receiver_id, content, is_read, created_at')
    .eq('id', id)
    .eq('receiver_id', userId)
    .eq('is_deleted_by_receiver', false)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;

  const message = data as RawInboxMessage;
  const names = await nicknameMap([message.sender_id]);
  return {
    ...message,
    sender_nickname: names[message.sender_id] || '(탈퇴 회원)',
  };
}

export async function markMessageRead(id: number | string): Promise<void> {
  const userId = await currentUserId();
  const { error } = await supabase
    .from('messages')
    .update({ is_read: true })
    .eq('id', id)
    .eq('receiver_id', userId);
  if (error) throw error;
}

export async function getUnreadMessageCount(): Promise<number> {
  const userId = await currentUserId();
  const { count, error } = await supabase
    .from('messages')
    .select('id', { count: 'exact', head: true })
    .eq('receiver_id', userId)
    .eq('is_deleted_by_receiver', false)
    .eq('is_read', false);
  if (error) throw error;
  return count || 0;
}
